import { RefObject, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { useResizeObserver } from './useResizeObserver';

export type WidthBreakpoints<E = string> = [E, number][];

export function useWidthBreakpoints<T extends HTMLElement, E = string>(
    ref: RefObject<T>,
    breakpoints: WidthBreakpoints<E>,
    defaultValue: E
): E {
    const [size, setSize] = useState<E>();

    const onResize = () => {
        if (!ref.current) {
            return;
        }

        const width = ref.current.offsetWidth;

        let prevSize: number | null = null;

        for (const breakpoint of breakpoints) {
            const [sizeName, sizeWidth] = breakpoint;

            if (prevSize !== null && sizeWidth > prevSize) {
                throw new Error('breakpoints must be sorted desc');
            }

            if (width >= sizeWidth) {
                if (size !== sizeName) {
                    setSize(sizeName);
                }
                break;
            }

            prevSize = sizeWidth;
        }
    };

    useResizeObserver(ref, onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useIsomorphicLayoutEffect(onResize, []);

    return size || defaultValue;
}
